import * as React from 'react';
import { makeTestId } from '../utils/Declarative';
import { findIndex } from '../utils/FindIndex';
import { mergeEventHandlers, PropsAndElementAttrs } from '../utils/ReactHelpers';
import { ButtonProps } from './Button/ButtonWidget';

export enum SelectableButtonGroupSelectionBehavior {
  /** Multiple buttons can be selected within the group. */
  Multiple = 'Multiple',
  /** A single button (at most) can be selected within the group. */
  Single = 'Single',
}

export interface SelectableButtonGroupProps {
  /**
   * Array of `ButtonWidget` which will be rendered within the group. The following props on each child will be internally overridden:
   * `disableTabAccessibility`, `handleClick`, `ref`, `role`, `selected`.
   */
  children: Array<React.ReactElement<ButtonProps>>;

  /** The function that will be called when a button's selection state changes. */
  onChange?: (index: number, selected: boolean) => void;

  /**
   * The selection behavior for the button group. Options include:
   *
   *   - SelectableButtonGroupSelectionBehavior.Multiple: multiple buttons can be selected
   *   - SelectableButtonGroupSelectionBehavior.Single: a single button (at most) can be selected
   *
   * NOTE: For SelectableButtonGroupSelectionBehavior.Single select behavior, if multiple buttons are
   * selected, single selectiveness is not enforced.
   */
  selectionBehavior: SelectableButtonGroupSelectionBehavior;

  /** The overriding style of the button group element. */
  style?: React.CSSProperties;

  /** Test id to be attached to the button group. '-SelectableButtonGroup' will be automatically added as a suffix. */
  testId: string;

  /** A description of what the button group represents. This will also be used for the default `aria-label` of the group. */
  title: string;
}

export interface SelectableButtonGroupState {
  /** The index of the button that currently has focus, or should receive focus when navigating to this widget. */
  focusIndex: number;
}

export class SelectableButtonGroupWidget extends React.Component<PropsAndElementAttrs<SelectableButtonGroupProps>, SelectableButtonGroupState> {
  private childButtons: React.ReactElement<ButtonProps>[] = [];
  private buttonElements: HTMLElement[] = [];

  public constructor(props: SelectableButtonGroupProps) {
    super(props);

    this.updateChildButtons();
    this.state = { focusIndex: this.getInitialFocusIndex() };
  }

  public componentDidUpdate(prevProps: SelectableButtonGroupProps, prevState: SelectableButtonGroupState): void {
    const newFocusIndex = this.getInitialFocusIndex();
    if (prevState.focusIndex < 0 && newFocusIndex >= 0) {
      this.setState({ focusIndex: newFocusIndex });
    }
  }

  private updateChildButtons(): void {
    const childButtons: React.ReactElement<ButtonProps>[] = [];
    let selectedButtonsCount = 0;

    React.Children.map(this.props.children, (child: React.ReactElement<ButtonProps>) => {
      if (child.props.buttonType !== undefined) {
        childButtons.push(child);
        if (child.props.selected) { selectedButtonsCount++; }
      }
    });

    if (childButtons.length < 2) {
      throw Error(`Invalid button count: 2+ are required`);
    }

    if (this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single && selectedButtonsCount > 1) {
      // eslint-disable-next-line max-len
      throw Error(`Invalid selected button count: SelectableButtonGroupSelectionBehavior.Single requires one or zero buttons to be selected. ${selectedButtonsCount} are selected`);
    }

    this.childButtons = childButtons;
  }

  private getInitialFocusIndex(): number {
    let focusIndex = findIndex(this.childButtons, button => !!button.props.selected && !button.props.disabled);
    if (focusIndex < 0) {
      focusIndex = findIndex(this.childButtons, button => !button.props.disabled);
    }
    return focusIndex;
  }

  private isButtonSelected(index: number): boolean {
    return !!this.childButtons[index].props.selected;
  }

  private handleClick = (e: React.SyntheticEvent<HTMLDivElement>): void => {
    const buttonElement = e.currentTarget as HTMLElement;
    const buttonIndex = findIndex(this.childButtons, (button, index) => buttonElement === this.buttonElements[index]);
    const selected = this.childButtons[buttonIndex].props.selected;

    if (this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single && selected) { return; }

    if (this.props.onChange) {
      this.props.onChange(buttonIndex, !selected);
    }
  }

  private handleFocus: React.FocusEventHandler<HTMLElement> = mergeEventHandlers(e => {
    const target = e.target as HTMLElement;
    const focusIndex = findIndex(this.childButtons, (button, index) => target === this.buttonElements[index] && !button.props.disabled);

    if (focusIndex >= 0 && focusIndex !== this.state.focusIndex) {
      this.setState({ focusIndex });
    }
  }, e => this.props.onFocus?.(e));

  private handleKeyDown: React.KeyboardEventHandler<HTMLElement> = mergeEventHandlers(e => {
    const buttonCount = this.childButtons.length;

    let increment = 0;
    let focusIndex = this.state.focusIndex;

    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      increment = -1;
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      increment = 1;
    } else if (e.key === 'Home') {
      focusIndex = -1;
      increment = 1;
    } else if (e.key === 'End') {
      focusIndex = buttonCount;
      increment = -1;
    } else {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    do {
      focusIndex = (focusIndex + increment + buttonCount) % buttonCount;
      if (focusIndex === this.state.focusIndex) { return; }
    } while (this.childButtons[focusIndex].props.disabled);

    const isSingleSelect = this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single;
    if (isSingleSelect && !this.isButtonSelected(focusIndex) && this.props.onChange) {
      this.props.onChange(focusIndex, true);
    }

    if (focusIndex !== this.state.focusIndex) {
      this.setState({ focusIndex }, () => this.buttonElements[this.state.focusIndex]?.focus());
    }
  }, e => this.props.onKeyDown?.(e));

  private renderChildren(): React.ReactNode {
    return React.Children.map(this.props.children, (child: React.ReactElement<ButtonProps>, buttonIndex) => {
      if (child.props.buttonType === undefined) {
        return child;
      }

      const buttonPropsOverride: Partial<ButtonProps> | React.ClassAttributes<HTMLDivElement> = {
        buttonRef: buttonElem => buttonElem && (this.buttonElements[buttonIndex] = buttonElem),
        disableTabAccessibility: this.state.focusIndex !== buttonIndex,
        handleClick: this.handleClick,
        key: buttonIndex,
        role: this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single ? 'radio' : 'button',
        selected: !!child.props.selected
      };

      return React.cloneElement(child, buttonPropsOverride);
    });
  }

  public render(): JSX.Element {
    const { children, onChange, selectionBehavior, testId, ...htmlAttrs } = this.props;

    this.updateChildButtons();

    const isSingleSelect = this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single;

    return (
      <div
        aria-label={this.props.title}
        aria-multiselectable={!isSingleSelect || undefined}
        data-tb-test-id={makeTestId(this.props.testId, 'SelectableButtonGroup')}
        role={isSingleSelect ? 'radiogroup' : undefined}
        {...htmlAttrs}
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
      >
        {this.renderChildren()}
      </div>
    );
  }
}
